import { Signal } from "../core/Signal.js";
import { event } from "../util/EventUtil.js";
import { KeyCode } from "../util/KeyCode.js";
import { object } from "../util/ObjectUtil.js";
import { TabTrapPushOptions, tabTrap } from "../util/TabTrap.js";


export interface ModalHash {
    hash: string;
    reOpen: () => void;
}

export interface ModalOptions {
    /**
     * The content of the modal.
     */
    content: string | HTMLElement | ((info: Modal) => string | HTMLElement);
    /**
     * The title of the modal.
     */
    title?: string;
    /**
     * When closable is false, you need a close button in your content.
     * That button should have the attribute data-modal-action="close".
     */
    closable?: boolean;
    tabTrap?: Partial<TabTrapPushOptions>;
    layout?: "full" | "center";
    width?: number | string;
    height?: number | string;
    maxWidth?: number | string;
    maxHeight?: number | string;
    contentPadding?: number;
    chromeless?: boolean;
    history?: ModalHash;
    /**
     * To add multiple CSS classes, separate them with a space.
     */
    className?: string;

    onInit?: (modal: Modal) => void;
    onDispose?: (modal: Modal) => void;
    onClose?: (modal: Modal) => void;
    onHide?: (modal: Modal) => void;
    onShow?: (modal: Modal) => void;
    onRemove?: (modal: Modal) => void;
}

export class Modal {

    public readonly onDispose = Signal.create<[Modal]>();
    public readonly onClose = Signal.create<[Modal]>();
    public readonly onHide = Signal.create<[Modal]>();
    public readonly onShow = Signal.create<[Modal]>();
    public readonly onRemove = Signal.create<[Modal]>();

    public readonly element: HTMLElement;
    public readonly options: ModalOptions;

    private _content: HTMLElement;
    private _disposables: DisposableObject[] = [];
    private _removeTimeout: any = null;

    constructor(options: ModalOptions) {
        options = object.merge(
            {
                closable: true,
                layout: "center",
                title: "",
                width: 500,
                height: "auto",
                maxHeight: "auto",
                maxWidth: "auto",
                contentPadding: 16,
                chromeless: false
            }, options);

        this.options = options;

        this.element = document.createElement("div");

        if (options.width) {
            const w = typeof options.width === "number" ? (options.width + "px") : options.width;
            this.element.style.setProperty("--e5-modal__box__width", w);
        }

        if (options.height) {
            const h = typeof options.height === "number" ? (options.height + "px") : options.height;
            this.element.style.setProperty("--e5-modal__box__height", h);
        }

        if (options.maxWidth) {
            const w = typeof options.maxWidth === "number" ? (options.maxWidth + "px") : options.maxWidth;
            this.element.style.setProperty("--e5-modal__box__maxWidth", w);
        }

        if (options.maxHeight) {
            const h = typeof options.maxHeight === "number" ? (options.maxHeight + "px") : options.maxHeight;
            this.element.style.setProperty("--e5-modal__box__maxHeight", h);
        }

        if (typeof options.contentPadding === "number") {
            this.element.style.setProperty("--e5-modal__content__padding", options.contentPadding + "px");
        }

        this.element.className = "e5-modal e5-modal--closed e5-modal--layout-" + options.layout;
        if (options.className) {
            this.element.classList.add(...options.className.split(" "));
        }
        this.element.setAttribute("role", "dialog");
        this.element.setAttribute("aria-modal", "true");
        this.element.innerHTML = `
        <div class="e5-modal__background"></div>
        <div class="e5-modal__box">
            <div class="e5-modal__toolbar">
                <div class="e5-modal__toolbar-title"><span></span></div>
                <div class="e5-modal__toolbar-fill"></div>
                <button class="e5-modal__toolbar-button" data-modal-action="close">
                    <i class="fal fa-times"></i>
                </button>
            </div>
            <div class="e5-modal__content"></div>
        </div>
        `;

        if (options.chromeless) {
            this.element.classList.add("e5-modal--chromeless");
        }

        this._content = this.element.querySelector(".e5-modal__content");

        this.closable = options.closable;
        if (options.title) {
            this.title = options.title;
        }

        let content: string | HTMLElement = null;
        if (typeof options.content === "function") {
            content = options.content(this);
        } else {
            content = options.content;
        }
        if (typeof content === "string") {
            this._content.innerHTML = content;
        }
        else {
            this._content.innerHTML = "";
            this._content.appendChild(content);
        }

        let keyRepeat = false;
        let keyRepeatTime = 0;

        this._disposables.push(
            event.on({
                scope: this.element,
                type: "click",
                selector: `[data-modal-action="close"]`,
                listener: (e) => {
                    if (!this.visible) {
                        return;
                    }

                    /**
                     * Prevent holding key down triggers multiple 
                     * times a click event. When the modal is opened by keyboard (enter, space)
                     * and the user holds the key down, the modal will close immediately when a 
                     * close button is focused.
                     */
                    if (keyRepeat
                        && Date.now() - keyRepeatTime < 400) {
                        return;
                    }

                    this.close();
                }
            })
        );

        this._disposables.push(
            event.on({
                scope: document,
                type: "keydown",
                listener: (e) => {

                    if (KeyCode.is(e.event, KeyCode.ENTER, KeyCode.SPACE)) {
                        keyRepeat = e.event.repeat;
                        if (keyRepeat) {
                            keyRepeatTime = Date.now();
                        }
                    }

                    if (!this.visible) {
                        return;
                    }

                    if (e.event.repeat) {
                        return;
                    }

                    if (KeyCode.is(e.event, KeyCode.ESCAPE)) {
                        if (this.options.closable) {
                            this.close();
                        }
                    }
                },
            })
        );

        if (typeof options.onDispose === "function") {
            this.onDispose.add(options.onDispose);
        }

        if (typeof options.onClose === "function") {
            this.onClose.add(options.onClose);
        }

        if (typeof options.onHide === "function") {
            this.onHide.add(options.onHide);
        }

        if (typeof options.onShow === "function") {
            this.onShow.add(options.onShow);
        }

        if (typeof options.onRemove === "function") {
            this.onRemove.add(options.onRemove);
        }

        if (typeof options.onInit === "function") {
            options.onInit(this);
        }
    }

    public close(): void {
        this.onClose.dispatch(this);
    }

    public get title(): string {
        return this.element.querySelector(".e5-modal__toolbar-title>span").textContent;
    }

    public set title(value: string) {
        this.element.querySelector(".e5-modal__toolbar-title>span").textContent = value;
    }

    public get closable(): boolean {
        return this.element.classList.contains("e5-modal--closable");
    }

    public set closable(value: boolean) {
        this.element.classList.toggle("e5-modal--closable", value);
        this.element.querySelector(".e5-modal__background").setAttribute("data-modal-action", value ? "close" : "none");
    }

    public get visible(): boolean {
        return !this.element.classList.contains("e5-modal--closed")
    }

    public set visible(value: boolean) {
        if (this.visible == value) {
            return;
        }
        this.element.classList.toggle("e5-modal--closed", !value);

        if (value) {
            document.body.appendChild(this.element);
        } else {
            if (this._removeTimeout) {
                clearTimeout(this._removeTimeout);
            }
            this._removeTimeout = setTimeout(() => {
                if (!this.visible
                    && this.element.parentNode) {
                    this.element.remove();
                    this.onRemove.dispatch(this);
                }
            }, 400);
        }

        if (value) {
            if (this.options.tabTrap) {
                tabTrap.push(
                    object.merge(this.options.tabTrap, {
                        element: this.element
                    })
                );
            } else {
                tabTrap.push(
                    {
                        element: this.element
                    }
                );
            }

        } else {
            tabTrap.pop();
        }

        if (value) {
            this.onShow.dispatch(this);
        } else {
            this.onHide.dispatch(this);
        }
    }

    public dispose(): void {
        this.onDispose.dispatch(this);

        let m = this as Mutable<this>;
        m.element.remove();
        m.element = null;
        m.options = null;
        m.onRemove.dispose();
        m.onRemove = null;
        m.onHide.dispose();
        m.onHide = null;
        m.onDispose.dispose();
        m.onDispose = null;
        m.onClose.dispose();
        m.onClose = null;
        m.onShow.dispose();
        m.onShow = null;
        this._content = null;

        if (this._removeTimeout) {
            clearTimeout(this._removeTimeout);
            this._removeTimeout = null;
        }

        for (let d of this._disposables) {
            d.dispose();
        }
        this._disposables = null;
    }
}

class ModalManager {
    private _stack: Modal[] = [];
    private _current: Modal = null;
    private _ignoreNextHistoryChange: boolean = false;
    private _hashes: {
        [key: string]: {
            hash: ModalHash;
            modal: Modal;
        }
    } = {};

    constructor() {
        // window.addEventListener("popstate", (e) => {
        //     const hash = window.location.hash.substring(1);
        //     console.log("POPSTATE", e,e.state, hash);
        //     if (this._hashes[hash]) {
        //         this._hashes[hash].modal.close();
        //     }
        // });

        window.addEventListener("hashchange", (e) => {

            if (this._ignoreNextHistoryChange) {
                this._ignoreNextHistoryChange = false;
                return;
            }

            let oldHash = new URL(e.oldURL).hash;
            if (oldHash) {
                oldHash = oldHash.substring(1);
            }
            let newHash = new URL(e.newURL).hash;
            if (newHash) {
                newHash = newHash.substring(1);
            }

            if (oldHash == newHash) {
                return;
            }

            if (oldHash && this._hashes[oldHash]
                && this._hashes[oldHash].modal
            ) {
                this._hashes[oldHash].modal.close();
                this._hashes[oldHash].modal = null;
            }
            else if (newHash && this._hashes[newHash]) {
                this._hashes[newHash].hash.reOpen();
            }
        });
    }

    public show(options: ModalOptions): Modal {



        let modal = new Modal(options);
        modal.onClose.add(() => {

            const index = this._stack.indexOf(modal);

            if (this.current === modal) {
                this.current = this._stack[index - 1] || null;
            }

            this._stack.splice(index, 1);

            setTimeout(() => {
                modal.dispose();
                modal = null;
            }, 400);
        });



        this._stack.push(modal);

        this.current = modal;

        if (options.history) {

            const urlBefore = window.location.href;
            this._hashes[options.history.hash] = {
                hash: options.history,
                modal: modal
            };

            let oldHash = new URL(window.location.href).hash;

            if (oldHash != "#" + options.history.hash) {
                window.history.pushState(null, null, "#" + options.history.hash);
            }
            modal.onClose.add(() => {
                let oldHash = new URL(window.location.href).hash;
                if (oldHash && "#" + options.history.hash) {
                    this._ignoreNextHistoryChange = true;
                    window.history.back();
                }
            });
        }

        return modal;
    }

    public clearStack(): void {
        this._stack.forEach(m => m.close());
    }

    private get current(): Modal {
        return this._current;
    }

    private set current(value: Modal) {
        if (this._current == value) {
            return;
        }
        if (this._current) {
            this._current.visible = false;
        }
        this._current = value;
        if (this._current) {
            this._current.visible = true;
            document.documentElement.style.setProperty("--e5-modal__scrollbarWidth",
                (window.innerWidth - document.documentElement.clientWidth) + "px");
            document.documentElement.classList.add("e5-modal__root--open");
        } else {
            setTimeout(() => {
                if (!this._current) {
                    document.documentElement.classList.remove("e5-modal__root--open");
                }
            }, 400);
        }
    }
}

export const modal = new ModalManager();